// Turns rows pasted from a spreadsheet into accounts with monthly balances.
// Accepts the loose shape of a personal-finance sheet: tag columns such as "BS", "$", "£",
// "Savings", a percentage, then an account name, then one balance per month.

import { Currency, AccountBucket, AccountSide } from '../types/personalForecast';
import { addMonths, currentMonth } from './forecastEngine';

export interface ImportedRow {
  name: string;
  currency: Currency;
  bucket: AccountBucket;
  side: AccountSide;
  /** Month-end balances keyed YYYY-MM. Liabilities are stored as positive amounts. */
  balances: Record<string, number>;
  /** A "65%"-style cell if the row had one. Not used yet; kept so nothing is lost. */
  afterTaxPercent?: number;
}

export interface ImportResult {
  rows: ImportedRow[];
  /** Human-readable reasons for lines that produced nothing. */
  skipped: string[];
  /** Months beyond `lastMonth` that were present and dropped (future columns). */
  droppedFutureColumns: number;
}

const CURRENCY_TOKENS: Record<string, Currency> = {
  '$': 'CAD', 'c$': 'CAD', 'ca$': 'CAD', 'cad': 'CAD',
  'us$': 'USD', 'usd': 'USD',
  '£': 'GBP', 'gbp': 'GBP',
  '€': 'EUR', 'eur': 'EUR',
};

const BUCKET_TOKENS: Record<string, AccountBucket> = {
  current: 'current', chequing: 'current', checking: 'current',
  savings: 'savings', saving: 'savings',
  retirement: 'retirement', pension: 'retirement',
};

type Cell =
  | { kind: 'blank' }
  | { kind: 'tag' }
  | { kind: 'currency'; value: Currency }
  | { kind: 'bucket'; value: AccountBucket; raw: string }
  | { kind: 'percent'; value: number }
  | { kind: 'number'; value: number | null }
  | { kind: 'text'; value: string };

/** "(1,234)" and "-1,234" are negatives; "-" alone is an empty month. */
export function parseAmount(raw: string): number | null | undefined {
  const text = raw.trim();
  if (/^[-–—]+$/.test(text)) return null;
  const negative = /^\(.*\)$/.test(text) || /^-/.test(text);
  const digits = text.replace(/[()\s,$£€]/g, '').replace(/^-/, '').replace(/^(US|C|CA|CAD|USD|GBP|EUR)/i, '');
  if (!/^\d+(\.\d+)?$/.test(digits)) return undefined;
  const value = parseFloat(digits);
  return negative ? -value : value;
}

function classify(raw: string): Cell {
  const text = raw.trim();
  if (text === '') return { kind: 'blank' };
  const lower = text.toLowerCase();
  if (lower === 'bs' || lower === 'cf') return { kind: 'tag' };
  if (lower in CURRENCY_TOKENS) return { kind: 'currency', value: CURRENCY_TOKENS[lower] };
  if (lower in BUCKET_TOKENS) return { kind: 'bucket', value: BUCKET_TOKENS[lower], raw: text };
  if (/^-?\d+(\.\d+)?\s*%$/.test(text)) return { kind: 'percent', value: parseFloat(text) };
  const amount = parseAmount(text);
  if (amount !== undefined) return { kind: 'number', value: amount };
  return { kind: 'text', value: text };
}

/**
 * @param text        the pasted block (tab- or comma-separated, one account per line)
 * @param firstMonth  YYYY-MM for the first balance column; later columns are consecutive months
 * @param baseCurrency used when a row names no currency
 * @param lastMonth   balances for months after this are dropped (defaults to the current month)
 */
export function parseHistoryPaste(
  text: string,
  firstMonth: string,
  baseCurrency: Currency,
  lastMonth: string = currentMonth()
): ImportResult {
  const rows: ImportedRow[] = [];
  const skipped: string[] = [];
  let droppedFutureColumns = 0;

  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  for (const line of lines) {
    const separator = line.includes('\t') ? '\t' : ',';
    const cells = line.split(separator).map(classify);

    let name = '';
    let bucketWord = '';
    let currency = baseCurrency;
    let bucket: AccountBucket = 'savings';
    let afterTaxPercent: number | undefined;
    const amounts: Array<number | null> = [];

    for (const cell of cells) {
      if (cell.kind === 'currency') currency = cell.value;
      else if (cell.kind === 'bucket') { bucket = cell.value; bucketWord = cell.raw; }
      else if (cell.kind === 'percent') afterTaxPercent = cell.value;
      else if (cell.kind === 'number') amounts.push(cell.value);
      else if (cell.kind === 'text' && !name) name = cell.value;
      else if (cell.kind === 'blank' && amounts.length > 0) amounts.push(null);
    }

    // "Chequing" or "Pension" on its own is the account's name as well as a hint about its bucket.
    if (!name && bucketWord) name = bucketWord;

    const recorded = amounts.filter((a): a is number => a !== null);
    if (!name) { skipped.push(`No account name: "${line.trim().slice(0, 60)}"`); continue; }
    if (recorded.length === 0) { skipped.push(`No balances for "${name}"`); continue; }

    const negatives = recorded.filter(a => a < 0).length;
    const side: AccountSide = negatives > recorded.length / 2 ? 'liability' : 'asset';

    const balances: Record<string, number> = {};
    amounts.forEach((amount, index) => {
      if (amount === null) return;
      const month = addMonths(firstMonth, index);
      if (month > lastMonth) { droppedFutureColumns += 1; return; }
      balances[month] = side === 'liability' ? Math.abs(amount) : amount;
    });

    if (Object.keys(balances).length === 0) { skipped.push(`All balances for "${name}" fall after ${lastMonth}`); continue; }
    rows.push({ name, currency, bucket, side, balances, ...(afterTaxPercent !== undefined ? { afterTaxPercent } : {}) });
  }

  return { rows, skipped, droppedFutureColumns };
}
